import { FC, useEffect, useMemo, useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import { clsx } from "clsx";
import { Button, Form, Input, Modal, Switch, message } from "../../design-system";
import { handleError } from "../ErrorHandling";
import { InviteUsersResponse, UserInvite, UserRole } from "../../types/UserGroup";
import classes from "./InviteUsersModal.module.less";

/** How long the identity provider leaves an invitation open. */
const EXPIRY_DAYS = 7;

/** Lenient enough for a pasted list, strict enough to catch a typo. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One address the box yielded, as the list below it reads it. */
interface Entry {
    email: string;
    valid: boolean;
}

/**
 * Reads the box. One address per line, but commas and semicolons separate too,
 * so a row copied out of a spreadsheet parses, and a line pasted out of a mail
 * client ("Bo Chen <bo@acme.com>") counts as its address. Repeats are dropped:
 * the list shows each person once.
 */
const parseEntries = (text: string): Entry[] => {
    const seen = new Set<string>();
    const entries: Entry[] = [];
    for (const token of text.split(/[\n,;]/)) {
        const trimmed = token.trim();
        if (!trimmed) continue;
        const angled = trimmed.match(/<([^>]*)>/);
        const email = (angled ? angled[1] : trimmed).trim().toLowerCase();
        if (!email || seen.has(email)) continue;
        seen.add(email);
        entries.push({ email, valid: EMAIL.test(email) });
    }
    return entries;
};

const inviteCount = (count: number) => `${count} ${count === 1 ? "invite" : "invites"}`;

export interface InviteUsersModalProps {
    open: boolean;
    /** Closes the dialog, whether anything was sent or not. */
    onClose: () => void;
    /** At least one invitation went out, so the caller's list is stale. */
    onSent: () => void;
    /**
     * Sends the batch: the app invites into the signed-in admin's own
     * organization, the ops dashboard into whichever one it is looking at.
     */
    invite: (invites: UserInvite[]) => Promise<InviteUsersResponse>;
    title?: string;
}

/**
 * Invites people into an organization, a list at a time: paste addresses
 * into the box, then switch on the ones who arrive as admins. Admin is the
 * only role an invite can grant: plain user is what everybody gets, and
 * superadmin is employee-only and stripped by the backend.
 */
export const InviteUsersModal: FC<InviteUsersModalProps> = ({
    open,
    onClose,
    onSent,
    invite,
    title = "Invite members",
}) => {
    const [text, setText] = useState("");
    // The addresses ticked as admin; everyone else arrives as a plain member.
    const [admins, setAdmins] = useState<Set<string>>(() => new Set());
    // Why an address the server refused could not be invited, kept per address
    // so a batch that half worked says which half.
    const [failures, setFailures] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    // The dialog stays mounted between openings, so it clears itself each time
    // it opens rather than leaving the last batch in the box.
    useEffect(() => {
        if (!open) return;
        setText("");
        setAdmins(new Set());
        setFailures({});
    }, [open]);

    const entries = useMemo(() => parseEntries(text), [text]);
    const sendable = entries.filter((entry) => entry.valid);

    const setAdmin = (email: string, admin: boolean) =>
        setAdmins((current) => {
            const next = new Set(current);
            if (admin) next.add(email);
            else next.delete(email);
            return next;
        });

    // Removing rewrites the box from what is left, so the box and the list stay
    // the same thing. It costs the pasted line its decoration, which is only
    // ever a name the invitation does not carry anyway.
    const remove = (email: string) =>
        setText(
            entries
                .filter((entry) => entry.email !== email)
                .map((entry) => entry.email)
                .join("\n")
        );

    const send = async () => {
        setSaving(true);
        try {
            // One call for the batch. The server sends the invitations one at a
            // time and reports both halves, so a refused address costs only
            // itself.
            const { invited, failed } = await invite(
                sendable.map((entry) => ({
                    email: entry.email,
                    roles: admins.has(entry.email) ? [UserRole.ADMIN] : [],
                }))
            );

            if (invited.length > 0) {
                message.success(`${inviteCount(invited.length)} sent.`);
                onSent();
            }
            if (failed.length === 0) {
                onClose();
                return;
            }
            // Stay open on what was refused, with the reason against each
            // address, so a typo can be fixed without retyping the list.
            setFailures(Object.fromEntries(failed.map(({ email, reason }) => [email, reason])));
            setText(failed.map(({ email }) => email).join("\n"));
            message.warning(`${inviteCount(failed.length)} could not be sent.`);
        } catch (error) {
            handleError(error);
        } finally {
            setSaving(false);
        }
    };

    const problemWith = (entry: Entry) =>
        entry.valid ? failures[entry.email] : "Not an email address.";

    return (
        <Modal
            title={title}
            open={open}
            width={640}
            onCancel={onClose}
            destroyOnClose
            footer={
                <div className={classes.footer}>
                    <span className={classes.note}>Invitations expire in {EXPIRY_DAYS} days.</span>
                    <div className={classes.actions}>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            disabled={sendable.length === 0}
                            loading={saving}
                            onClick={send}
                        >
                            {sendable.length === 0
                                ? "Send invites"
                                : `Send ${inviteCount(sendable.length)}`}
                        </Button>
                    </div>
                </div>
            }
        >
            <Form layout="vertical">
                <Form.Item
                    label="Email addresses"
                    extra="One per line. Paste a list to invite a group at once."
                    required
                >
                    <Input.TextArea
                        // Autofill off: an admin typing a colleague's address
                        // should not have a password manager offering to fill
                        // in their own.
                        autoComplete="off"
                        rows={5}
                        placeholder={"ana@acme.com\nbo@acme.com"}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                    />
                </Form.Item>
            </Form>

            {/* A table, so each address lines up with its own controls. It
                shows no rules of its own: the columns are an alignment, not a
                grid the reader should see. */}
            {entries.length > 0 && (
                <div className={classes.assignments}>
                    <table className={classes.table}>
                        <colgroup>
                            <col />
                            <col className={classes["admin-column"]} />
                            <col className={classes["remove-column"]} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>
                                    <span className={classes.count}>
                                        {inviteCount(sendable.length)} to send
                                    </span>
                                </th>
                                <th />
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry) => {
                                const problem = problemWith(entry);
                                return (
                                    <tr key={entry.email}>
                                        <td
                                            className={classes.address}
                                            colSpan={entry.valid ? 1 : 2}
                                        >
                                            <div
                                                className={clsx(
                                                    classes.email,
                                                    !entry.valid && classes.refused
                                                )}
                                            >
                                                {entry.email}
                                            </div>
                                            {problem && (
                                                <div className={classes.problem}>{problem}</div>
                                            )}
                                        </td>
                                        {/* An address the server refused keeps
                                            its control: the next click sends it
                                            again, so it stays adjustable. */}
                                        {entry.valid && (
                                            <td>
                                                <label className={classes.admin}>
                                                    <Switch
                                                        size="small"
                                                        checked={admins.has(entry.email)}
                                                        onChange={(checked: boolean) =>
                                                            setAdmin(entry.email, checked)
                                                        }
                                                    />
                                                    Admin
                                                </label>
                                            </td>
                                        )}
                                        <td>
                                            <Button
                                                type="text"
                                                className={classes.remove}
                                                icon={<DeleteOutlined />}
                                                aria-label={`Remove ${entry.email}`}
                                                onClick={() => remove(entry.email)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
};

InviteUsersModal.displayName = "InviteUsersModal";
